//
// Credit to Dave Stockley (magicspon)
// https://github.com/magicspon
//
import FormDataJson from 'form-data-json-convert/dist/form-data-json.es6.js';
import { flatMap, isPlainObject } from 'lodash-es';

export const getFormFieldMeta = (form) => {
    const allRows = flatMap(form.pages, 'rows');
    const allFields = flatMap(allRows, 'rowFields');

    const fields = flatMap(allFields, ({ handle, inputTypeName }) => ({ handle, inputTypeName }));

    return fields;
};

// Get file upload fields from the form
export const getFileUploadFields = (form) => {
    const allRows = flatMap(form.pages, 'rows');
    const allFields = flatMap(allRows, 'rowFields');

    const fileFields = allFields.filter(({ inputTypeName }) => inputTypeName === 'FileUploadInput' || inputTypeName === '[FileUploadInput]');

    return fileFields;
};

// Create file upload mutation for a specific field
export const getFileUploadMutation = (fieldHandle) => {
    return `
        mutation UploadFile($file: FileInput) {
            save_generalAssets_Asset(_file: $file) {
                id
                filename
                url
            }
        }
    `;
};

// Create file upload mutations for all file fields in a form
export const getFileUploadMutations = (form) => {
    const fileFields = getFileUploadFields(form);

    const mutations = fileFields.map((field) => ({
        handle: field.handle,
        mutation: getFileUploadMutation(field.handle),
        inputTypeName: field.inputTypeName,
    }));

    return mutations;
};

// Process file uploads and return file IDs for form submission
export const processFileUploads = async (form, formElement, gqlClient) => {
    const fileFields = getFileUploadFields(form);
    const uploadedFiles = {};

    for (const field of fileFields) {
        const fileInput = formElement.querySelector(`[name="${field.handle}"]`);

        if (fileInput && fileInput.files && fileInput.files.length > 0) {
            const files = Array.from(fileInput.files);
            const uploadPromises = files.map(async (file) => {
                const mutation = getFileUploadMutation(field.handle);

                // Convert file to FileUploadInput format
                const rawFileInput = {
                    filename: file.name,
                    fileData: await fileToBase64(file),
                };

                // Validate and clean the file input for Craft CMS
                const fileInput = validateFileInput(rawFileInput);

                try {
                    const result = await gqlClient.request(mutation, { file: fileInput });
                    console.log('process file uploads result', result);

                    return result.save_generalAssets_Asset;
                } catch (error) {
                    console.error(`Error uploading file ${file.name}:`, error);
                    console.error('File input data:', {
                        filename: fileInput.filename,
                        fileDataLength: fileInput.fileData ? fileInput.fileData.length : 0,
                        originalFileSize: file.size,
                        originalFileType: file.type,
                    });
                    throw error;
                }
            });

            const uploadedAssets = await Promise.all(uploadPromises);

            // Store file IDs for the form submission
            if (field.inputTypeName === '[FileUploadInput]') {
                uploadedFiles[field.handle] = uploadedAssets.map((asset) => asset.id);
            } else {
                uploadedFiles[field.handle] = uploadedAssets[0].id;
            }
        }
    }

    return uploadedFiles;
};

// Helper function to convert File to base64
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // Keep the full data URL prefix for CraftCMS
            const base64 = reader.result;
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
};

// Helper function to validate file input for Craft CMS FileUploadInput
const validateFileInput = (fileInput) => {
    if (!fileInput.filename || typeof fileInput.filename !== 'string') {
        throw new Error('FileUploadInput requires a valid filename string');
    }

    if (!fileInput.fileData || typeof fileInput.fileData !== 'string') {
        throw new Error('FileUploadInput requires valid fileData as base64 string');
    }

    // Ensure we only have the supported fields
    const validFields = ['filename', 'fileData'];
    const providedFields = Object.keys(fileInput);
    const invalidFields = providedFields.filter((field) => !validFields.includes(field));

    if (invalidFields.length > 0) {
        console.warn(`FileUploadInput received unsupported fields: ${invalidFields.join(', ')}. These will be ignored.`);
    }

    return {
        filename: fileInput.filename,
        fileData: fileInput.fileData,
    };
};

function createMutationHandle(form) {
    return `save_${form.handle}_Submission`;
}

function createMutationTypes(form) {
    const types = getFormFieldMeta(form).map(({ handle, inputTypeName }) => `$${handle}: ${inputTypeName}`);

    // Add in any captcha tokens generated when we queried the form.
    form.captchas.forEach((captcha) => {
        types.push(`$${captcha.handle}: FormieCaptchaInput`);
    });

    return types.join(', ');
}

function createMutationValues(form) {
    const values = flatMap(getFormFieldMeta(form), 'handle').map((key) => `${key}: $${key}`);

    // Add in any captcha tokens generated when we queried the form.
    form.captchas.forEach((captcha) => {
        values.push(`${captcha.handle}: $${captcha.handle}`);
    });

    return values.join(', ');
}

export const getFormMutation = (form) => {
    const mutationTypes = createMutationTypes(form);
    const mutationHandle = createMutationHandle(form);
    const mutationValues = createMutationValues(form);

    return `
        mutation FormMutation(${mutationTypes}) {
            ${mutationHandle}(${mutationValues}) {
                id
            }
        }
    `;
};

export const getMutationVariables = async (form, el) => {
    const object = FormDataJson.toJson(el);

    // Get the mutation types to ensure we cast everything properly
    const mutationTypes = getFormFieldMeta(form);

    console.log('mutationTypes', mutationTypes);

    for (const info of mutationTypes) {
        let value = object[info.handle];
        console.log('value of', info.handle, value);

        // if (info.inputTypeName === 'FileUploadInput') {
        //     console.log('Single FileUploadInput', info, value);
        //     // For single file upload, convert File object to base64 if needed
        //     if (value && value instanceof File) {
        //         // Convert File object to base64
        //         const base64Data = await fileToBase64(value);
        //         value = {
        //             filename: value.name,
        //             fileData: base64Data,
        //         };
        //     } else if (value && value.fileData) {
        //         // Already processed, ensure it's in the correct format
        //         value = {
        //             filename: value.filename,
        //             fileData: value.fileData,
        //         };
        //     }
        // }

        if (info.inputTypeName === '[FileUploadInput]') {
            const fileInput = el.querySelector(`[name="${info.handle}"]`);
            console.log('Multiple FileUploadInput', info, value);
            // Handle multiple file uploads - convert File objects to base64 if needed
            const processedFiles = [];
            if (fileInput && fileInput.files && fileInput.files.length > 0) {
                const files = Array.from(fileInput.files);
                const uploadPromises = files.map(async (file) => {
                    // Convert File object to base64
                    const base64Data = await fileToBase64(file);
                    processedFiles.push({
                        filename: file.name,
                        fileData: base64Data,
                    });
                });
                await Promise.all(uploadPromises);
            }
            value = processedFiles;

            console.log('processedFiles', processedFiles, 'wow');
        }

        if (typeof value === 'undefined') {
            continue;
        }

        // Fix up any objects that look like arrays
        if (isPlainObject(value)) {
            if (typeof value[0] !== 'undefined') {
                value = Object.values(value);
            }
        }

        if (info.inputTypeName === 'Int') {
            value = parseInt(object[info.handle], 10);
        }

        if (info.inputTypeName === '[Int]') {
            if (isPlainObject(value)) {
                value = Object.values(value);
            }

            value = value.map((item) => parseInt(item, 10));
        }

        if (info.inputTypeName === 'Number') {
            value = Number(object[info.handle]);
        }

        if (info.inputTypeName === '[Number]') {
            if (isPlainObject(value)) {
                value = Object.values(value);
            }

            value = value.map((item) => Number(item));
        }

        object[info.handle] = value;
    }

    // Add in any captcha tokens generated when we queried the form.
    form.captchas.forEach((captcha) => {
        object[captcha.handle] = {
            name: captcha.name,
            value: captcha.value,
        };
    });

    return object;
};

// Enhanced getMutationVariables that includes uploaded file IDs
export const getMutationVariablesWithFiles = async (form, el, uploadedFiles = {}) => {
    const object = await getMutationVariables(form, el);

    // Replace file upload fields with their uploaded file IDs
    const fileFields = getFileUploadFields(form);
    fileFields.forEach((field) => {
        if (uploadedFiles[field.handle]) {
            object[field.handle] = uploadedFiles[field.handle];
        }
    });

    return object;
};
